import React, { useState } from 'react';
import { cva } from 'class-variance-authority';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-xl font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative overflow-hidden",
  {
    variants: {
      variant: {
        primary: "bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 focus:ring-primary-400 shadow-sm hover:shadow-md",
        secondary: "bg-neutral-200 text-neutral-900 hover:bg-neutral-300 active:bg-neutral-400 focus:ring-neutral-400",
        success: "bg-success-500 text-white hover:bg-success-600 active:bg-success-700 focus:ring-success-400 shadow-sm hover:shadow-md",
        danger: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus:ring-red-500 shadow-sm hover:shadow-md",
        warning: "bg-amber-600 text-white hover:bg-amber-700 active:bg-amber-800 focus:ring-amber-500 shadow-sm hover:shadow-md",
        outline: "border border-neutral-300 bg-transparent text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 focus:ring-neutral-400",
        ghost: "hover:bg-neutral-100 active:bg-neutral-200 focus:ring-neutral-400",
        link: "text-primary-600 underline-offset-4 hover:underline focus:ring-primary-400 p-0",
      },
      size: {
        sm: "h-9 px-3 text-sm",
        md: "h-10 px-4 py-2",
        lg: "h-11 px-6 text-base",
        xl: "h-14 px-8 text-xl",
        icon: "h-10 w-10 p-0",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export const Button = React.forwardRef(
  ({
    className,
    variant,
    size,
    fullWidth,
    loading,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ripple = true,
    onClick,
    ...props
  }, ref) => {
    const [ripples, setRipples] = useState([]);

    const handleClick = (e) => {
      if (ripple && !disabled && !loading) {
        const button = e.currentTarget;
        const rect = button.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const newRipple = { x, y, id: Date.now() };
        setRipples((prev) => [...prev, newRipple]);
        setTimeout(() => {
          setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
        }, 600);
      }
      if (onClick) onClick(e);
    };

    return (
      <button
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={loading || disabled}
        onClick={handleClick}
        {...props}
      >
        {ripple && ripples.map((r) => (
          <motion.span
            key={r.id}
            className="absolute rounded-full bg-white opacity-30"
            style={{ left: r.x, top: r.y, width: 0, height: 0 }}
            initial={{ width: 0, height: 0 }}
            animate={{ width: 200, height: 200, x: -100, y: -100 }}
            transition={{ duration: 0.6 }}
          />
        ))}

        <span className="relative z-10 flex items-center justify-center">
          {loading && (
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {!loading && leftIcon && <span className="mr-2" aria-hidden="true">{leftIcon}</span>}
          {children}
          {!loading && rightIcon && <span className="ml-2" aria-hidden="true">{rightIcon}</span>}
        </span>
      </button>
    );
  }
);

Button.displayName = "Button";
